import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/components/language-provider";
import { Code, Shield, Webhook, Terminal, MessageCircle, ArrowRight, Building2, Globe, Key } from "lucide-react";
import { WEBSITE_ROUTES } from "@/lib/config";
import { AnimatedSection } from "@/components/landing/animations";
import { PageHeader } from "@/components/page-header";

export default function ApiDocs() {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />

      {/* Hero */}
      <section className="py-16 sm:py-20 md:py-28 blob-bg">
        <div className="container mx-auto px-4">
          <AnimatedSection>
            <Badge className="mb-4 font-medium" style={{ background: "hsla(95, 40%, 38%, 0.08)", color: "var(--fs-forest)" }}>
              <Terminal className="w-3 h-3 mr-1.5" />REST API
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{language === "pt" ? "API de Integração" : language === "es" ? "API de integración" : "Integration API"}</h1>
            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
              {language === "pt"
                ? "Conecte o FillSign ao seu sistema — seja um CRM, ERP, portal web ou qualquer plataforma que sua empresa utilize."
                : language === "es" ? "Conecte FillSign a su sistema, ya sea un CRM, un ERP, un portal web o cualquier plataforma que use su empresa." : "Connect FillSign to your system — whether it's a CRM, ERP, web portal, or any platform your company uses."}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Main explanation */}
      <section className="py-16 sm:py-20 md:py-28">
        <div className="container mx-auto px-4 max-w-4xl">
          <AnimatedSection>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">
              {language === "pt" ? "Integre com qualquer sistema" : language === "es" ? "Integre con cualquier sistema" : "Integrate with any system"}
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                {language === "pt"
                  ? "Se sua empresa utiliza outros CRMs, ERPs ou sistemas internos além do Dynamics 365, a API REST do FillSign permite integrar assinaturas digitais diretamente no seu fluxo de trabalho."
                  : language === "es" ? "Si su empresa usa otros CRM, ERP o sistemas internos además de Dynamics 365, la API REST de FillSign le permite integrar firmas digitales directamente en su flujo de trabajo." : "If your company uses other CRMs, ERPs, or internal systems beyond Dynamics 365, the FillSign REST API lets you integrate digital signatures directly into your workflow."}
              </p>
              <p>
                {language === "pt"
                  ? "Com a API você pode enviar documentos para assinatura, acompanhar o status em tempo real, receber notificações via webhook quando um documento é assinado, e fazer download dos documentos finalizados — tudo de forma programática."
                  : language === "es" ? "Con la API puede enviar documentos para firma, seguir el estado en tiempo real, recibir notificaciones por webhook cuando se firma un documento y descargar los documentos finalizados, todo de forma programática." : "With the API you can send documents for signing, track status in real time, receive webhook notifications when a document is signed, and download finalized documents — all programmatically."}
              </p>
            </div>
          </AnimatedSection>

          {/* API Key distinction */}
          <AnimatedSection delay={0.15} className="mt-12">
            <div className="card-organic p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "hsla(42, 75%, 45%, 0.1)", color: "var(--fs-amber)" }}>
                  <Key className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">{language === "pt" ? "API Keys independentes" : language === "es" ? "API Keys independientes" : "Independent API Keys"}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {language === "pt"
                      ? "A API Key gerada para integrações via REST API é independente e separada da API Key do plugin Dynamics 365, que é exclusiva para o ambiente CRM. Isso garante segurança e controle granular sobre cada tipo de integração."
                      : language === "es" ? "La API Key generada para integraciones por API REST es independiente de la API Key del plugin de Dynamics 365, que es exclusiva del entorno CRM. Esto garantiza seguridad y control granular sobre cada tipo de integración." : "The API Key generated for REST API integrations is independent and separate from the Dynamics 365 plugin API Key, which is exclusive to the CRM environment. This ensures security and granular control over each integration type."}
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 sm:py-20 md:py-28" style={{ background: "linear-gradient(180deg, hsl(var(--muted) / 0.3) 0%, transparent 100%)" }}>
        <div className="container mx-auto px-4 max-w-4xl">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold">{language === "pt" ? "O que a API oferece" : language === "es" ? "Lo que ofrece la API" : "What the API offers"}</h2>
          </AnimatedSection>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
            {[
              { icon: Code, title: language === "pt" ? "Envio de Documentos" : language === "es" ? "Envío de documentos" : "Document Sending", desc: language === "pt" ? "Envie PDFs e DOCXs para assinatura via chamadas HTTP simples" : language === "es" ? "Envíe PDF y DOCX para firma con llamadas HTTP simples" : "Send PDFs and DOCXs for signing via simple HTTP calls" },
              { icon: Globe, title: language === "pt" ? "Status em Tempo Real" : language === "es" ? "Estado en tiempo real" : "Real-time Status", desc: language === "pt" ? "Consulte o andamento de cada documento e signatário" : language === "es" ? "Consulte el avance de cada documento y firmante" : "Check the progress of each document and signer" },
              { icon: Webhook, title: "Webhooks", desc: language === "pt" ? "Receba notificações automáticas a cada evento de assinatura" : language === "es" ? "Reciba notificaciones automáticas en cada evento de firma" : "Receive automatic notifications for every signing event" },
              { icon: Shield, title: "OAuth 2.0", desc: language === "pt" ? "Autenticação segura para todas as chamadas" : language === "es" ? "Autenticación segura en todas las llamadas" : "Secure authentication for all calls" },
              { icon: Building2, title: "SDKs", desc: language === "pt" ? "Bibliotecas para C#, Python e Node.js" : language === "es" ? "Bibliotecas para C#, Python y Node.js" : "Libraries for C#, Python, and Node.js" },
              { icon: Terminal, title: language === "pt" ? "Sandbox" : language === "es" ? "Sandbox" : "Sandbox", desc: language === "pt" ? "Ambiente de testes para desenvolvimento seguro" : language === "es" ? "Entorno de pruebas para un desarrollo seguro" : "Test environment for safe development" },
            ].map((f, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="card-organic p-5 text-center h-full">
                  <f.icon className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--fs-forest)" }} />
                  <h3 className="font-semibold text-sm mb-1">{f.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 md:py-28 blob-bg">
        <div className="container mx-auto px-4 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{language === "pt" ? "Precisa integrar?" : language === "es" ? "¿Necesita integrar?" : "Need to integrate?"}</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              {language === "pt"
                ? "Entre em contato e receba acesso à documentação técnica completa e credenciais de sandbox."
                : language === "es" ? "Contáctenos y reciba acceso a la documentación técnica completa y a credenciales de sandbox." : "Contact us and get access to the full technical documentation and sandbox credentials."}
            </p>
            <Link href={WEBSITE_ROUTES.enterpriseContact}>
              <button className="btn-cta flex items-center gap-2 mx-auto">
                <MessageCircle className="w-4 h-4" />{language === "pt" ? "Solicitar Acesso" : language === "es" ? "Solicitar acceso" : "Request Access"}
              </button>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <footer className="py-8 border-t text-center text-sm text-muted-foreground">
        <div className="container mx-auto px-4">© {new Date().getFullYear()} FillSign.</div>
      </footer>
    </div>
  );
}
